import React, { useEffect, useMemo, useState } from 'react';
import { InputNumber, SelectPicker } from 'rsuite';

import { useEmployeeRetirement, useSavePayrollRetirement } from '../../hooks/useQueries';
import { showToast } from '../../utils/toast';
import RetirementEmpowerModal from './RetirementEmpowerModal';

// One employee's 401(k) and Roth 401(k) deferral elections, shown on their own
// record beside their pay rate - which is what the figures are calculated
// against, so this is the screen where the two can be checked together.
//
// Rendered both for the employee on their own profile, where `canManage` is
// true because the election is theirs to make, and for an admin editing
// somebody's record. The server enforces the same rule, so a `canManage` passed
// wrongly here gets a 403 rather than a write.
//
// Like PayRateHistory, this owns its own read, edit state and save rather than
// joining the profile form: the election is its own record with its own
// permission, and folding it into the profile payload would tie saving a phone
// number to saving a deferral.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.
// Removing the module means deleting this file and the block that renders it in
// MePage's Compensation card.

const MODE_OPTIONS = [
  { label: 'None', value: '' },
  { label: '% of comp', value: 'percent' },
  { label: 'Flat $', value: 'amount' },
];

const PLANS = [
  { key: 'traditional', label: '401(k)' },
  { key: 'roth', label: 'Roth 401(k)' },
];

const toForm = (election) => ({
  traditionalMode: election?.traditionalMode || '',
  traditionalValue: election?.traditionalValue ?? '',
  rothMode: election?.rothMode || '',
  rothValue: election?.rothValue ?? '',
});

const sameForm = (a, b) =>
  a.traditionalMode === b.traditionalMode
  && String(a.traditionalValue) === String(b.traditionalValue)
  && a.rothMode === b.rothMode
  && String(a.rothValue) === String(b.rothValue);

const fmtMoney = (value) =>
  Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// How the election reads when it is not being edited. An unset plan says so in
// words rather than showing a dash, because "no deferral" is a decision.
const describe = (mode, value) => {
  if (mode === 'percent') return `${Number(value)}% of compensation`;
  if (mode === 'amount') return `${fmtMoney(value)} per pay period`;
  return 'Not contributing';
};

// Each plan's election as a card, in the same card style as PATO's Used and
// Remaining, for showing beside them while the section is closed. Same query
// as RetirementElection, so it adds no request.
const shortDescribe = (mode, value) => {
  if (mode === 'percent') return `${Number(value)}%`;
  if (mode === 'amount') return `${fmtMoney(value)}/period`;
  return 'None';
};

export function RetirementUsageSummary({ employeeId }) {
  const query = useEmployeeRetirement(employeeId, { enabled: Boolean(employeeId) });
  if (!query.data) return null;
  const election = query.data.election || null;
  return (
    <div className="me-pato__usage">
      {PLANS.map((plan) => (
        <div key={plan.key} className="me-pato__usageitem">
          <span className="me-pato__usagelabel">{plan.label}</span>
          <span className="me-pato__usagevalue bdc-num">
            {shortDescribe(election?.[`${plan.key}Mode`], election?.[`${plan.key}Value`])}
          </span>
        </div>
      ))}
    </div>
  );
}

// `isSelf` decides whether the Empower reminder is raised after a save. An
// admin editing somebody else's election has no Empower account to mirror it
// in, and cannot answer for whether the employee has been there - so they are
// not asked, and the employee is told by the email payroll receives instead.
export default function RetirementElection({ employeeId, canManage, isSelf = false }) {
  const query = useEmployeeRetirement(employeeId, { enabled: Boolean(employeeId) });
  const save = useSavePayrollRetirement();

  const election = query.data?.election || null;
  const matchPercent = query.data?.employerMatchPercent;

  const baseline = useMemo(() => toForm(election), [election]);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(baseline);
  const [error, setError] = useState(null);
  // Raised on a successful save and cleared only by acknowledging it.
  const [empowerOpen, setEmpowerOpen] = useState(false);

  // A refetch under the form resets it, but not mid-edit, where it would
  // discard what was typed.
  useEffect(() => {
    if (isEditing) return;
    setForm(baseline);
  }, [baseline, isEditing]);

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      // Clearing the type clears the number with it, so a stale value cannot
      // sit behind a "None" and reappear if the type is set again.
      if (field === 'traditionalMode' && !value) next.traditionalValue = '';
      if (field === 'rothMode' && !value) next.rothValue = '';
      return next;
    });
    setError(null);
  };

  const startEdit = () => {
    setForm(baseline);
    setError(null);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setForm(baseline);
    setError(null);
    setIsEditing(false);
  };

  const handleSave = async () => {
    // A type with no number would defer nothing while looking like it defers
    // something, so it is refused here rather than stored.
    for (const plan of PLANS) {
      const mode = form[`${plan.key}Mode`];
      const value = form[`${plan.key}Value`];
      if (mode && (value === '' || value == null)) {
        setError(`${plan.label} is set to ${mode === 'percent' ? 'a percentage' : 'a flat amount'} but has no value.`);
        return;
      }
    }

    try {
      await save.mutateAsync({
        employeeId,
        traditionalMode: form.traditionalMode || null,
        traditionalValue: form.traditionalMode ? Number(form.traditionalValue) : null,
        rothMode: form.rothMode || null,
        rothValue: form.rothMode ? Number(form.rothValue) : null,
      });
      setIsEditing(false);
      showToast('success', 'Retirement election saved.');
      // Only after the save actually succeeded - being told to go and mirror a
      // change that did not stick would send them to Empower for nothing.
      if (isSelf) setEmpowerOpen(true);
    } catch (err) {
      setError(err?.message || 'Could not save the election.');
    }
  };

  const dirty = !sameForm(form, baseline);

  if (query.isLoading) {
    return <div className="me-retirement__loading">Loading retirement election…</div>;
  }

  // The module's table may not exist yet on this database. Say so plainly
  // rather than showing an empty card that looks like a real "no deferral".
  if (query.isError) {
    return (
      <div className="me-retirement__error">
        Retirement elections are unavailable: {query.error?.message || 'the request failed.'}
      </div>
    );
  }

  return (
    <div className="me-retirement">
      <RetirementEmpowerModal open={empowerOpen} onConfirm={() => setEmpowerOpen(false)} />

      <div className="me-retirement__head">
        <div className="me-field__label">Retirement</div>
        {canManage && !isEditing ? (
          <button type="button" className="bdc-btn bdc-btn--sm" onClick={startEdit}>
            <i className="bi bi-pencil" aria-hidden="true" />
            Edit
          </button>
        ) : null}
      </div>

      {error ? <div className="me-retirement__error">{error}</div> : null}

      <div className="me-retirement__plans">
        {PLANS.map((plan) => (
          <div className="me-retirement__plan" key={plan.key}>
            <div className="me-retirement__planName">{plan.label}</div>

            {isEditing ? (
              <div className="me-retirement__inputs">
                <SelectPicker
                  data={MODE_OPTIONS}
                  value={form[`${plan.key}Mode`]}
                  onChange={(value) => setField(`${plan.key}Mode`, value ?? '')}
                  cleanable={false}
                  searchable={false}
                  size="sm"
                  style={{ width: 140 }}
                />
                <InputNumber
                  value={form[`${plan.key}Value`]}
                  onChange={(value) => setField(`${plan.key}Value`, value)}
                  disabled={!form[`${plan.key}Mode`]}
                  min={0}
                  max={form[`${plan.key}Mode`] === 'percent' ? 100 : undefined}
                  step={form[`${plan.key}Mode`] === 'percent' ? 1 : 50}
                  size="sm"
                  prefix={form[`${plan.key}Mode`] === 'amount' ? '$' : undefined}
                  postfix={form[`${plan.key}Mode`] === 'percent' ? '%' : undefined}
                  style={{ width: 130 }}
                />
              </div>
            ) : (
              <div
                className={`me-retirement__value${election?.[`${plan.key}Mode`] ? '' : ' me-retirement__value--none'}`}
              >
                {describe(election?.[`${plan.key}Mode`], election?.[`${plan.key}Value`])}
              </div>
            )}
          </div>
        ))}
      </div>

      {isEditing ? (
        <div className="me-retirement__actions">
          <button
            type="button"
            className="bdc-btn bdc-btn--sm bdc-btn--primary"
            onClick={handleSave}
            disabled={save.isPending || !dirty}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="bdc-btn bdc-btn--sm"
            onClick={cancelEdit}
            disabled={save.isPending}
          >
            Cancel
          </button>
        </div>
      ) : null}

      {matchPercent != null ? (
        <div className="me-retirement__note">
          The company matches the combined deferral up to <strong>{matchPercent}%</strong> of total compensation, and
          only as far as the employee contributes. That rate is the same for everyone and is set by payroll.
        </div>
      ) : null}
    </div>
  );
}
